import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import {
  LuBuilding2,
  LuCalendar,
  LuCheck,
  LuCrown,
  LuEye,
  LuFileImage,
  LuLoaderCircle,
  LuShieldCheck,
  LuTrash2,
  LuUpload,
} from "react-icons/lu";

import TextInput from "../../common/TextInput";
import Button from "../../common/Buttons";
import Modal from "../../common/Modal";
import ConfirmDialog from "../../common/ConfirmDialog";
import Toaster, { notify } from "../../common/Toast";
import {
  fetchOrganization,
  updateOrganization,
  deactivateOrganization,
  uploadOrgLogo,
  removeOrgLogo,
} from "../../redux/action/organizationAction/OrganizationAction";

applyPlugin(jsPDF);

const EMPTY_FORM = { name: "", address: "", taxId: "" };

function Pill({ icon: Icon, children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${tones[tone]}`}>
      {Icon && <Icon size={12} />}
      {children}
    </span>
  );
}

function LogoCard({ title, description, logoUrl, onUpload, onRemove, uploading, removing, extraAction }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file) => {
    if (file && file.type.startsWith("image/")) onUpload(file);
  };

  return (
    <div className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
      <div className="border-b border-slate-100 bg-slate-50/60 px-4 py-3">
        <h3 className="text-[13px] font-semibold text-slate-800">{title}</h3>
        <p className="text-[11px] text-slate-400">{description}</p>
      </div>

      <div className="p-4">
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className={`flex h-32 w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed p-2 text-center transition ${
            isDragging
              ? "border-brand-400 bg-brand-50/60"
              : logoUrl
                ? "border-transparent bg-slate-50"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          {logoUrl ? (
            <img src={logoUrl} alt={title} className="h-full w-full object-contain" />
          ) : (
            <>
              <LuUpload size={18} className="text-slate-300" />
              <p className="text-[11.5px] font-medium text-slate-500">Click or drag an image here</p>
            </>
          )}
        </label>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            icon={<LuFileImage size={14} />}
            loading={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {logoUrl ? "Replace" : "Upload"}
          </Button>
          {logoUrl && (
            <button
              type="button"
              onClick={onRemove}
              disabled={removing}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-[13px] font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {removing ? <LuLoaderCircle size={14} className="animate-spin" /> : <LuTrash2 size={14} />}
              Remove
            </button>
          )}
          {extraAction}
        </div>
      </div>
    </div>
  );
}

export default function Organization() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const {
    info,
    infoLoading,
    updateLoading,
    deactivateLoading,
    logoUploading,
    logoRemoving,
  } = useSelector((state) => state.organization);

  const [form, setForm] = useState(EMPTY_FORM);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);

  useEffect(() => {
    dispatch(fetchOrganization()).then((res) => {
      if (!res.success) notify.error("Failed to fetch organization info", res.error);
    });
  }, [dispatch]);

  useEffect(() => {
    if (info) {
      setForm({
        name: info.name || "",
        address: info.address || "",
        taxId: info.taxId || "",
      });
    }
  }, [info]);

  const isDirty =
    info &&
    (form.name !== (info.name || "") ||
      form.address !== (info.address || "") ||
      form.taxId !== (info.taxId || ""));

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify.error("Name is required", "Organization name cannot be empty.");
      return;
    }
    const res = await dispatch(updateOrganization(form));
    if (res.success) {
      notify.success("Organization Updated", "Your organization information has been saved.");
      dispatch(fetchOrganization());
    } else {
      notify.error("Failed to update organization", res.error);
    }
  };

  const handleDeactivate = async () => {
    const res = await dispatch(deactivateOrganization());
    setDeactivateOpen(false);
    if (res.success) {
      notify.success("Organization Deactivated", "Please contact support for reactivation.");
      setTimeout(() => window.location.reload(), 1500);
    } else {
      notify.error("Failed to deactivate organization", res.error);
    }
  };

  const handleLogoUpload = async (file, key, fileName) => {
    const res = await dispatch(uploadOrgLogo(file, key, fileName, user?.organizationId));
    if (res.success) {
      notify.success("Logo Uploaded", "Your organization logo has been updated.");
      dispatch(fetchOrganization());
    } else {
      notify.error("Failed to upload logo", res.error);
    }
  };

  const handleLogoRemove = async (key, fileName) => {
    const res = await dispatch(removeOrgLogo(key, fileName, user?.organizationId));
    if (res.success) {
      notify.success("Logo Removed", "Your organization logo has been removed.");
      dispatch(fetchOrganization());
    } else {
      notify.error("Failed to remove logo", res.error);
    }
  };

  // .................... Sample PO preview (unchanged business logic) ...........

  const generateSamplePO = async () => {
    const doc = new jsPDF("p", "mm", "a4", true);

    function checkPageBreak() {
      const pageHeight = doc.internal.pageSize.getHeight();
      if (y + 6 > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }
    }
    function incrementY(v) {
      y += v;
      checkPageBreak();
    }
    function wrapText(text, x, maxWidth) {
      doc.splitTextToSize(text || "", maxWidth).forEach((line) => {
        doc.text(line, x, y);
        incrementY(6);
      });
    }

    let x = info?.poLogoUrl ? 65 : 14;
    let y = 28;

    doc.setFontSize(16);
    doc.setFont(undefined, "bolditalic");
    doc.text("Purchase Order #1", x, 16);
    doc.setFont(undefined, "normal");

    if (info?.poLogoUrl) {
      try {
        const response = await fetch(info.poLogoUrl, { credentials: "include" });
        const blob = await response.blob();
        const img = new Image();
        img.src = URL.createObjectURL(blob);
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        doc.addImage(img, "JPEG", 14, 25, 45, 40, "", "FAST");
        URL.revokeObjectURL(img.src);
      } catch {
        // logo fetch failed — proceed without it
      }
    }

    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("Ship to:", x, y);
    wrapText(info?.name, x + 17, 120);
    doc.setFont(undefined, "normal");
    doc.text("Address:", x, y);
    wrapText(info?.address, x + 17, 120);
    doc.text("Tax ID:", x, y);
    doc.text(info?.taxId || "", x + 17, y);
    incrementY(6);
    doc.text("Phone #:", x, y);
    doc.text("Email:", x + 60, y);
    incrementY(6);
    doc.text("Contact:", x, y);
    doc.text(user?.name || "", x + 17, y);
    incrementY(10);

    doc.setFont(undefined, "bold");
    doc.text("Bill to:", x, y);
    wrapText(info?.name, x + 17, 120);
    doc.setFont(undefined, "normal");
    doc.text("Address:", x, y);
    wrapText(info?.address, x + 17, 120);
    doc.text("Tax ID:", x, y);
    doc.text(info?.taxId || "", x + 17, y);
    incrementY(10);

    doc.text("Order Date:", 14, y);
    doc.text(new Date().toLocaleDateString(), 40, y);
    doc.text("Quotation #:", 91, y);
    incrementY(6);
    doc.text("PO Author:", 14, y);
    doc.text(user?.name || "", 40, y);
    doc.text("Project:", 91, y);
    wrapText("Sample Project", 117, 94);

    doc.autoTable({
      startY: y,
      head: [["Item Name", "Manufacturer", "Catalog", "# Units", "Unit Price", "Discount", "Tax", "Subtotal"]],
      body: [["Sample Item A", "Sample Manufacturer A", "Sample Catalog #A", 10, 10.0, "10.00%", "10.00%", "100.00"]],
      theme: "grid",
    });

    y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : 100;

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("Grand Total: 100.00", 141, y);
    doc.setFont(undefined, "normal");
    incrementY(6);

    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("Supplier :", 14, y);
    incrementY(6);
    doc.setFont(undefined, "normal");
    doc.text("Address:", 14, y);
    incrementY(6);
    doc.text("GST:", 14, y);
    incrementY(6);
    doc.text("Phone #:", 14, y);
    doc.text("Email:", 74, y);
    incrementY(6);
    doc.text("Contact:", 14, y);
    incrementY(10);

    doc.setFont(undefined, "bold");
    doc.text("Additional Notes:", 14, y);
    doc.setFont(undefined, "normal");
    incrementY(6);
    doc.text("none", 18, y);

    return doc;
  };

  const handlePoPreview = async () => {
    setGeneratingPreview(true);
    try {
      const doc = await generateSamplePO();
      setPreviewUrl(URL.createObjectURL(doc.output("blob")));
      setPreviewOpen(true);
    } catch (error) {
      notify.error("Failed to generate PO preview", error?.message);
    } finally {
      setGeneratingPreview(false);
    }
  };

  return (
    <div className="min-h-full w-full bg-gray-50 pb-10">
      <div className="mx-auto w-full px-3 py-4 sm:px-4 lg:px-6">
        {/* ---------------------------- Header ---------------------------- */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-r from-brand-50/80 via-white to-white shadow-sm">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3.5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-brand">
                <LuBuilding2 size={22} />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-slate-900">
                  {info?.name || "Organization"}
                </h1>
                <p className="mt-0.5 text-[13px] text-slate-500">
                  Manage your organization's profile and purchase order branding.
                </p>
                {info && (
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <Pill icon={LuShieldCheck} tone={info.isActive ? "green" : "red"}>
                      {info.isActive ? "Active" : "Inactive"}
                    </Pill>
                    {info.premiumMember && (
                      <Pill icon={LuCrown} tone="amber">
                        Premium
                      </Pill>
                    )}
                    {info.createdAt && (
                      <Pill icon={LuCalendar}>
                        Member since {new Date(info.createdAt).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                      </Pill>
                    )}
                  </div>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setDeactivateOpen(true)}
              className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-red-200 bg-white px-3.5 text-[13px] font-medium text-red-600 shadow-sm transition hover:bg-red-50"
            >
              Deactivate
            </button>
          </div>
        </div>

        {infoLoading && !info ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <LuLoaderCircle size={20} className="animate-spin text-gray-300" />
            <p className="text-[12.5px] text-gray-400">Loading organization…</p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* ---------------------------- Logos ---------------------------- */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <LogoCard
                title="Main Logo"
                description="Shown across the app and on your account."
                logoUrl={info?.accountLogoUrl}
                uploading={logoUploading.mainLogo}
                removing={logoRemoving.mainLogo}
                onUpload={(file) => handleLogoUpload(file, "mainLogo", "main-logo")}
                onRemove={() => handleLogoRemove("mainLogo", "main-logo")}
              />
              <LogoCard
                title="Purchase Order Logo"
                description="Printed on the header of every generated PO."
                logoUrl={info?.poLogoUrl}
                uploading={logoUploading.poLogo}
                removing={logoRemoving.poLogo}
                onUpload={(file) => handleLogoUpload(file, "poLogo", "purchase-order-logo")}
                onRemove={() => handleLogoRemove("poLogo", "purchase-order-logo")}
                extraAction={
                  <button
                    type="button"
                    onClick={handlePoPreview}
                    disabled={generatingPreview}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {generatingPreview ? <LuLoaderCircle size={14} className="animate-spin" /> : <LuEye size={14} />}
                    Preview PO
                  </button>
                }
              />
            </div>

            {/* ---------------------------- Details ---------------------------- */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-4 py-3">
                <div>
                  <h3 className="text-[13px] font-semibold text-slate-800">Organization Details</h3>
                  <p className="text-[11px] text-slate-400">Used on invoices, purchase orders, and your account profile.</p>
                </div>
                {isDirty && (
                  <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-medium text-amber-700">
                    Unsaved changes
                  </span>
                )}
              </div>

              <div className="p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <TextInput
                      label="Name"
                      required
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Organization name"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <TextInput
                      label="Address"
                      value={form.address}
                      onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                      placeholder="Address"
                    />
                  </div>
                  <TextInput
                    label="Tax ID"
                    value={form.taxId}
                    onChange={(e) => setForm((f) => ({ ...f, taxId: e.target.value }))}
                    placeholder="Tax identification number"
                  />
                </div>

                <div className="mt-5 flex justify-end border-t border-slate-100 pt-4">
                  <Button
                    variant="solid"
                    icon={<LuCheck size={14} />}
                    disabled={!isDirty || !form.name.trim()}
                    loading={updateLoading}
                    onClick={handleSave}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Sample Purchase Order">
        <div className="h-[75vh] w-full">
          {previewUrl && (
            <iframe src={previewUrl} title="Sample PO" className="h-full w-full rounded-lg border border-slate-200" />
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivate}
        title="Deactivate organization?"
        description="Your organization will be deactivated and inaccessible until reactivated by support. This action cannot be undone by you."
        confirmLabel="Deactivate"
        tone="danger"
        loading={deactivateLoading}
      />

      <Toaster />
    </div>
  );
}